import { useEffect, useState } from 'react';
import { api } from './api';

/* Real species photos via the backend's Wikipedia lookup.
 * Cached in memory and localStorage (7 days), max 4 requests in flight.
 */
const TTL = 7 * 24 * 3600 * 1000;
const mem = new Map();
const pending = new Map();
const queue = [];
let active = 0;

function readCache(title) {
    if (mem.has(title)) return mem.get(title);
    try {
        const v = JSON.parse(localStorage.getItem(`gd_sp:${title}`) || 'null');
        if (v && Date.now() - v.t < TTL) { mem.set(title, v.d); return v.d; }
    } catch { /* ignore */ }
    return undefined;
}

function pump() {
    while (active < 4 && queue.length) {
        const { title, resolve } = queue.shift();
        active += 1;
        api.get('/species-photo', { params: { title } })
            .then((r) => r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : { url: null })
            .catch(() => ({ url: null, transient: true }))
            .then((d) => {
                active -= 1;
                mem.set(title, d);
                if (!d.transient) {
                    try { localStorage.setItem(`gd_sp:${title}`, JSON.stringify({ t: Date.now(), d })); } catch { /* ignore */ }
                }
                resolve(d);
                pump();
            });
    }
}

export function fetchSpeciesPhoto(title) {
    const cached = readCache(title);
    if (cached !== undefined) return Promise.resolve(cached);
    if (!pending.has(title)) {
        pending.set(title, new Promise((resolve) => { queue.push({ title, resolve }); pump(); }).finally(() => pending.delete(title)));
    }
    return pending.get(title);
}

/** Returns { url, thumb, page } once known, or null while loading / when there is none. */
export function useSpeciesPhoto(title, enabled = true) {
    const [data, setData] = useState(() => (title ? readCache(title) || null : null));
    useEffect(() => {
        if (!title || !enabled) return undefined;
        let alive = true;
        fetchSpeciesPhoto(title).then((d) => { if (alive) setData(d?.url ? d : null); });
        return () => { alive = false; };
    }, [title, enabled]);
    return data?.url ? data : null;
}
